import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import { useDispatch, useSelector } from "react-redux";
import {
  useForm,
  type UseFormRegister,
  type FieldErrors,
} from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CustomButton,
  CustomTextInput,
  CustomModal,
  CustomLoading,
  FixedActionBar,
  colors,
} from "@ippon/shared-components";
import { store, type AppDispatch, type RootState } from "@/store";
import {
  fetchManagementTaskCustomFields,
  createManagementTaskCustomField,
  updateManagementTaskCustomField,
  deleteManagementTaskCustomField,
  type ManagementTaskCustomFieldRecord,
  type ManagementTaskCustomFieldSection,
  type ManagementTaskCustomFieldType,
} from "@/store/managementTaskCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";

interface SectionDef {
  key: ManagementTaskCustomFieldSection;
  name: string;
  fixedFields: { id: string; label: string; required: boolean }[];
}

// 並びは実フォーム (ManagementTaskEditModal / ManagementResultEditDrawer) に合わせている。
// 順序を変える時は実フォーム側も同時に更新すること。
const SECTIONS: SectionDef[] = [
  {
    key: "task",
    name: "新規登録",
    fixedFields: [
      { id: "name", label: "項目名", required: true },
      { id: "task_type", label: "種別", required: true },
      { id: "is_legal_inspection", label: "法定点検 (点検タスクのみ)", required: false },
      { id: "description", label: "概要説明", required: false },
      { id: "related_laws", label: "関連法令", required: false },
      { id: "frequency", label: "頻度", required: true },
      { id: "frequency_count", label: "回数", required: false },
      // frequency_unit は内部値 (頻度から自動マッピング)、UI 非表示のため固定項目から除外
      { id: "target_areas", label: "場所区分", required: false },
      { id: "linked_units", label: "名称", required: false },
      { id: "contractor", label: "対応業者", required: false },
      { id: "remarks", label: "備考", required: false },
      { id: "attachments", label: "添付資料", required: false },
    ],
  },
  {
    key: "result",
    name: "予定/実績入力",
    fixedFields: [
      { id: "planned", label: "実施予定", required: false },
      { id: "planned_dates", label: "実施予定日", required: false },
      { id: "planned_is_remote", label: "リモート点検 (予定・点検タスクのみ)", required: false },
      { id: "executed", label: "実施済", required: false },
      { id: "actual_is_remote", label: "リモート点検 (実績・点検タスクのみ)", required: false },
      { id: "total_judgement", label: "総合判定結果", required: false },
      { id: "execution_date", label: "実施結果 - 日付", required: false },
      { id: "execution_time", label: "実施結果 - 時間", required: false },
      { id: "execution_checker", label: "実施結果 - 担当者", required: false },
      { id: "execution_result", label: "実施結果 - 点検結果", required: false },
      { id: "execution_findings", label: "実施結果 - 指摘事項", required: false },
      {
        id: "execution_attachments",
        label: "実施結果 - 添付資料",
        required: false,
      },
      { id: "execution_note", label: "実施結果 - 備考", required: false },
      { id: "remarks", label: "備考", required: false },
      { id: "other_attachments", label: "その他資料", required: false },
    ],
  },
];

const ACCORDION_SX = {
  border: "1px solid #d9d9d9",
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { borderRadius: "10px" },
} as const;

const ACCORDION_SUMMARY_SX = {
  px: 2,
  py: 0,
  minHeight: 50,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "7px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 50 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "7px 0" },
} as const;

interface DraftField {
  field_id: string;
  label: string;
  field_type: ManagementTaskCustomFieldType;
  options: string;
  dirty: boolean;
  isNew?: boolean;
  section_key?: ManagementTaskCustomFieldSection;
}

const fieldToDraft = (f: ManagementTaskCustomFieldRecord): DraftField => ({
  field_id: f.field_id,
  label: f.label,
  field_type: f.field_type,
  options: (f.options || []).join("\n"),
  dirty: false,
  section_key: f.section_key,
});

const normalizeDraftOptions = (options: string) =>
  options
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");

const isDraftDirtyFromRecord = (
  draft: DraftField,
  record?: ManagementTaskCustomFieldRecord,
) => {
  if (draft.isNew) return draft.label.trim().length > 0;
  if (!record) return draft.dirty;
  return (
    draft.label !== record.label ||
    draft.field_type !== record.field_type ||
    draft.section_key !== record.section_key ||
    normalizeDraftOptions(draft.options) !==
      normalizeDraftOptions((record.options || []).join("\n"))
  );
};

interface SortableFieldRowProps {
  fieldId: string;
  labelRegister: UseFormRegister<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  onDelete: () => void;
}

const SortableFieldRow: React.FC<SortableFieldRowProps> = ({
  fieldId,
  labelRegister,
  errors,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid="management-task-custom-field-row"
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#999",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextInput
            label=""
            name={`${fieldId}_label`}
            register={labelRegister}
            errors={errors}
            placeholder="項目名"
            hasSpacing={false}
            inputWidth="100%"
            inputWidthSp="100%"
          />
        </Box>
        <IconButton
          size="small"
          onClick={onDelete}
          title="削除"
          sx={{
            color: colors.text.secondary,
            flexShrink: 0,
            "&:hover": { color: colors.status.error },
          }}
        >
          <ButtonDeleteIcon width={14} height={16} />
        </IconButton>
      </Stack>
    </Box>
  );
};

const ManagementTaskCustomFormSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const items = useSelector(
    (s: RootState) => s.managementTaskCustomFields.list.data,
  );
  const loading = useSelector(
    (s: RootState) => s.managementTaskCustomFields.list.loading,
  );
  const loaded = useSelector(
    (s: RootState) => s.managementTaskCustomFields.list.loaded,
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => Object.fromEntries(SECTIONS.map((s) => [s.key, false])));
  const [expandedFixed, setExpandedFixed] = useState<Record<string, boolean>>(
    {},
  );
  const [drafts, setDrafts] = useState<Record<string, DraftField>>({});
  const [pendingDeleteFieldIds, setPendingDeleteFieldIds] = useState<string[]>(
    [],
  );
  const [footerDeleteConfirmOpen, setFooterDeleteConfirmOpen] = useState(false);
  const [footerSaving, setFooterSaving] = useState(false);
  const [orderOverride, setOrderOverride] = useState<Record<string, string[]>>(
    {},
  );

  const editForm = useForm<Record<string, unknown>>({ defaultValues: {} });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    void dispatch(fetchManagementTaskCustomFields());
  }, [dispatch]);

  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, DraftField> = {};
      items.forEach((it) => {
        const existing = prev[it.field_id];
        next[it.field_id] =
          existing && isDraftDirtyFromRecord(existing, it)
            ? { ...existing, dirty: true }
            : fieldToDraft(it);
      });
      // 新規 draft (isNew) はそのまま残す
      Object.values(prev).forEach((d) => {
        if (d.isNew) next[d.field_id] = d;
      });
      return next;
    });
  }, [items]);

  useEffect(() => {
    Object.values(drafts).forEach((d) => {
      const labelKey = `${d.field_id}_label`;
      if (editForm.getValues(labelKey) !== d.label) {
        editForm.setValue(labelKey, d.label);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, drafts]);

  useEffect(() => {
    const sub = editForm.watch((values, info) => {
      const name = info?.name;
      if (!name || !name.endsWith("_label")) return;
      const fieldId = name.slice(0, -"_label".length);
      const v = values[name];
      if (typeof v !== "string") return;
      setDrafts((p) => {
        const cur = p[fieldId];
        if (!cur || cur.label === v) return p;
        const record = items.find((it) => it.field_id === fieldId);
        const next = { ...cur, label: v };
        return {
          ...p,
          [fieldId]: {
            ...next,
            dirty: isDraftDirtyFromRecord(next, record),
          },
        };
      });
    });
    return () => sub.unsubscribe();
  }, [editForm, items]);

  const grouped = useMemo(() => {
    const map: Record<string, ManagementTaskCustomFieldRecord[]> = {};
    SECTIONS.forEach((s) => (map[s.key] = []));
    items.forEach((it) => {
      const key = it.section_key;
      if (!map[key]) map[key] = [];
      map[key].push(it);
    });
    Object.keys(map).forEach((k) => {
      const override = orderOverride[k];
      if (override && override.length > 0) {
        const orderMap = new Map(override.map((id, idx) => [id, idx]));
        map[k].sort((a, b) => {
          const ai = orderMap.get(a.field_id);
          const bi = orderMap.get(b.field_id);
          if (ai !== undefined && bi !== undefined) return ai - bi;
          if (ai !== undefined) return -1;
          if (bi !== undefined) return 1;
          return (a.display_order ?? 999) - (b.display_order ?? 999);
        });
      } else {
        map[k].sort(
          (a, b) => (a.display_order ?? 999) - (b.display_order ?? 999),
        );
      }
    });
    return map;
  }, [items, orderOverride]);

  // 並び替えは「更新」ボタンとは独立で即時 PATCH するため、hasDirtyDrafts には含めない。
  const hasDirtyDrafts = useMemo(
    () =>
      pendingDeleteFieldIds.length > 0 ||
      Object.values(drafts).some((d) => {
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === d.field_id),
        );
      }),
    [drafts, items, pendingDeleteFieldIds],
  );


  const handleAddCustomField = (sectionKey: ManagementTaskCustomFieldSection) => {
    const tempId = `__new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setDrafts((p) => ({
      ...p,
      [tempId]: {
        field_id: tempId,
        label: "",
        field_type: "text",
        options: "",
        dirty: true,
        isNew: true,
        section_key: sectionKey,
      },
    }));
  };

  const handleDeleteFieldClick = (fieldId: string) => {
    const draft = drafts[fieldId];
    if (draft?.isNew) {
      setDrafts((p) => {
        const { [fieldId]: _, ...rest } = p;
        return rest;
      });
      return;
    }
    setPendingDeleteFieldIds((p) => [...p, fieldId]);
  };

  // BE 既存 + ローカル新規 (isNew) を混在させて並び替えできるようにする。
  // 並び替え後の挙動 (工事一覧のステータスと同じ方針):
  //   - 即座に orderOverride を更新して UI 反映 (描画はローカル優先)
  //   - BE 既存項目の display_order は background で PATCH (await しない / refetch しない)
  //   - 「更新」ボタンとは独立。hasDirtyDrafts には含めない
  //   - 失敗時のみ toast + refetch で巻き戻し
  //   - ローカル新規項目は drafts 内なので、保存 (persistDraft) 時に override の
  //     位置から display_order を算出する
  const handleDragEndForSection = (
    sectionKey: ManagementTaskCustomFieldSection,
    event: DragEndEvent,
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const beItems = (grouped[sectionKey] || []).filter(
      (it) => !pendingDeleteFieldIds.includes(it.field_id),
    );
    const localItems = Object.values(drafts).filter(
      (d) => d.isNew && d.section_key === sectionKey,
    );
    type MergedItem = { field_id: string; isLocal: boolean };
    const merged: MergedItem[] = [
      ...beItems.map((it) => ({ field_id: it.field_id, isLocal: false })),
      ...localItems.map((d) => ({ field_id: d.field_id, isLocal: true })),
    ];
    const existingOverride = orderOverride[sectionKey];
    if (existingOverride && existingOverride.length > 0) {
      const orderMap = new Map(existingOverride.map((id, idx) => [id, idx]));
      merged.sort((a, b) => {
        const ai = orderMap.get(a.field_id);
        const bi = orderMap.get(b.field_id);
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return 0;
      });
    }

    const oldIndex = merged.findIndex((it) => it.field_id === active.id);
    const newIndex = merged.findIndex((it) => it.field_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered: MergedItem[] = arrayMove(merged, oldIndex, newIndex);

    setOrderOverride((p) => ({
      ...p,
      [sectionKey]: reordered.map((r: MergedItem) => r.field_id),
    }));

    // 同セクションにローカル draft (isNew、空/未保存両方) があれば PATCH をスキップ。
    //   - draft の位置が BE 既存項目の display_order スロットを占有した状態で BE 更新
    //     すると、draft が discard / 別位置に動いた時に display_order がずれる。
    //   - 「更新」ボタン押下時の persistOrderChanges で draft 保存後に order をまとめて反映する。
    if (localItems.length > 0) return;

    // BE 既存項目だけ background で PATCH
    const beUpdates: { fieldId: string; newOrder: number }[] = [];
    reordered.forEach((it, idx) => {
      if (it.isLocal) return;
      const be = beItems.find((b) => b.field_id === it.field_id);
      if (!be) return;
      const newOrder = idx + 1;
      if (be.display_order === newOrder) return;
      beUpdates.push({ fieldId: it.field_id, newOrder });
    });
    if (beUpdates.length === 0) return;
    Promise.all(
      beUpdates.map(({ fieldId, newOrder }) =>
        dispatch(
          updateManagementTaskCustomField({
            field_id: fieldId,
            body: { display_order: newOrder },
          }),
        ).unwrap(),
      ),
    ).catch((e) => {
      showToast({
        type: "error",
        message:
          (e as { message?: string })?.message ||
          "並び替えの保存に失敗しました",
      });
      // 失敗時は BE 真を再取得し override をクリアして元の順序に戻す
      void dispatch(fetchManagementTaskCustomFields());
      setOrderOverride((p) => {
        const { [sectionKey]: _, ...rest } = p;
        return rest;
      });
    });
  };

  const persistDraft = async (
    fieldId: string,
    draft: DraftField,
    opts?: { quiet?: boolean },
  ): Promise<boolean> => {
    if (!draft.label.trim()) {
      showToast({ type: "error", message: "項目名は必須です" });
      return false;
    }
    const optionsArr =
      draft.field_type === "select"
        ? draft.options
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined;
    if (
      draft.field_type === "select" &&
      (!optionsArr || optionsArr.length === 0)
    ) {
      showToast({
        type: "error",
        message: "選択フィールドは選択肢が 1 件以上必要です",
      });
      return false;
    }

    if (draft.isNew) {
      const sectionKey = draft.section_key ?? "task";
      const latest = store.getState().managementTaskCustomFields.list.data;
      // ローカル項目を DnD で並び替えていた場合は override の位置から
      // display_order を算出する。並び替えていなければ末尾に追加。
      const sectionOverride = orderOverride[sectionKey];
      const overrideIdx = sectionOverride?.indexOf(fieldId) ?? -1;
      const nextOrder =
        overrideIdx >= 0
          ? overrideIdx + 1
          : latest
              .filter((it) => it.section_key === sectionKey)
              .reduce((m, it) => Math.max(m, it.display_order ?? 0), 0) + 1;
      const r = await dispatch(
        createManagementTaskCustomField({
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
          section_key: sectionKey,
          display_order: nextOrder,
        }),
      );
      if (createManagementTaskCustomField.fulfilled.match(r)) {
        setDrafts((p) => {
          const { [fieldId]: _, ...rest } = p;
          return rest;
        });
        if (!opts?.quiet) {
          showToast({ type: "success", message: "項目を追加しました" });
        }
        return true;
      }
      showToast({
        type: "error",
        message: (r.payload as string) || "追加に失敗しました",
      });
      return false;
    }

    const r = await dispatch(
      updateManagementTaskCustomField({
        field_id: fieldId,
        body: {
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
        },
      }),
    );
    if (updateManagementTaskCustomField.fulfilled.match(r)) {
      setDrafts((p) => ({ ...p, [fieldId]: { ...p[fieldId], dirty: false } }));
      if (!opts?.quiet) {
        showToast({ type: "success", message: "更新しました" });
      }
      return true;
    }
    showToast({
      type: "error",
      message: (r.payload as string) || "更新に失敗しました",
    });
    return false;
  };

  // orderOverride に基づいて BE 既存項目の display_order を一括 PATCH する。
  // 新規作成された項目は persistDraft 側で override の位置から display_order を
  // 算出するため、ここでは BE 既存項目だけを対象にする。
  // (drafts が存在するときに handleDragEnd で skip した分を、save 時にまとめて反映する役割)
  const persistOrderChanges = async (): Promise<boolean> => {
    const latest = store.getState().managementTaskCustomFields.list.data;
    for (const [sectionKey, ids] of Object.entries(orderOverride)) {
      if (!ids || ids.length === 0) continue;
      for (let idx = 0; idx < ids.length; idx++) {
        const fid = ids[idx];
        const be = latest.find(
          (it) => it.field_id === fid && it.section_key === sectionKey,
        );
        if (!be) continue; // ローカル新規 (persistDraft 側で扱う) / 削除済み
        const targetOrder = idx + 1;
        if ((be.display_order ?? 0) === targetOrder) continue;
        const r = await dispatch(
          updateManagementTaskCustomField({
            field_id: fid,
            body: { display_order: targetOrder },
          }),
        );
        if (!updateManagementTaskCustomField.fulfilled.match(r)) {
          showToast({
            type: "error",
            message: (r.payload as string) || "並び替えの保存に失敗しました",
          });
          return false;
        }
      }
    }
    return true;
  };

  // ラベル未入力で空の新規行を drafts から取り除く。
  // BE には届かない (label 必須でエラー) ので、UI 上もリセット時に整理する。
  const dropEmptyNewDrafts = () => {
    setDrafts((p) => {
      const next: typeof p = {};
      for (const [k, v] of Object.entries(p)) {
        if (v.isNew && v.label.trim() === "") continue;
        next[k] = v;
      }
      return next;
    });
  };

  const runFooterSaveOnly = async () => {
    const ids = Object.keys(drafts).filter((id) => {
      const d = drafts[id];
      if (!d) return false;
      if (d.isNew) return d.label.trim().length > 0;
      return isDraftDirtyFromRecord(
        d,
        items.find((it) => it.field_id === id),
      );
    });
    if (ids.length === 0) return;
    setFooterSaving(true);
    try {
      for (const id of ids) {
        const ok = await persistDraft(id, drafts[id], { quiet: true });
        if (!ok) return;
      }
      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;
      setOrderOverride({});
      dropEmptyNewDrafts();
      await dispatch(fetchManagementTaskCustomFields());
      showToast({ type: "success", message: "更新しました" });
    } finally {
      setFooterSaving(false);
    }
  };

  const handleFooterUpdateClick = () => {
    if (pendingDeleteFieldIds.length > 0) {
      setFooterDeleteConfirmOpen(true);
      return;
    }
    void runFooterSaveOnly();
  };

  const confirmFooterDeleteAndSave = async () => {
    const toDelete = [...pendingDeleteFieldIds];
    const draftsSnapshot = { ...drafts };
    setFooterDeleteConfirmOpen(false);
    setFooterSaving(true);
    try {
      for (const fid of toDelete) {
        const r = await dispatch(deleteManagementTaskCustomField(fid));
        if (!deleteManagementTaskCustomField.fulfilled.match(r)) {
          showToast({
            type: "error",
            message: (r.payload as string) || "削除に失敗しました",
          });
          return;
        }
      }
      setPendingDeleteFieldIds([]);
      await dispatch(fetchManagementTaskCustomFields());

      const toSaveIds = Object.keys(draftsSnapshot).filter((id) => {
        if (toDelete.includes(id)) return false;
        const d = draftsSnapshot[id];
        if (!d) return false;
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === id),
        );
      });
      for (const id of toSaveIds) {
        const ok = await persistDraft(id, draftsSnapshot[id], { quiet: true });
        if (!ok) return;
      }
      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;
      setOrderOverride({});
      dropEmptyNewDrafts();
      await dispatch(fetchManagementTaskCustomFields());
      showToast({ type: "success", message: "更新しました" });
    } finally {
      setFooterSaving(false);
    }
  };

  const cancelFooterDeleteConfirm = () => {
    setFooterDeleteConfirmOpen(false);
  };

  const toggleFixed = (key: string) =>
    setExpandedFixed((p) => ({ ...p, [key]: !p[key] }));

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          mt: 1,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        管理業務 (点検 / 清掃) の新規登録フォームと予定/実績入力ドロワーに追加する項目を管理します。
        固定項目はシステム既定で編集不可、追加項目はラベルを編集できます。
      </Typography>

      {loading && !loaded ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            width: "100%",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      ) : (
      <Stack spacing={1.5} useFlexGap sx={{ width: { xs: "100%", md: "60%" } }}>
        {SECTIONS.map((section) => {
          const beFields = (grouped[section.key] || []).filter(
            (it) => !pendingDeleteFieldIds.includes(it.field_id),
          );
          const localNewDrafts = Object.values(drafts).filter(
            (d) => d.isNew && d.section_key === section.key,
          );
          const customFields: Array<
            Pick<
              ManagementTaskCustomFieldRecord,
              "field_id" | "label" | "field_type"
            >
          > = [
            ...beFields,
            ...localNewDrafts.map((d) => ({
              field_id: d.field_id,
              label: d.label,
              field_type: d.field_type,
            })),
          ];
          // BE 項目とローカル項目を混在で並び替え可能にするため、
          // override が存在する場合はマージ後リストにも適用する。
          const sectionOverride = orderOverride[section.key];
          if (sectionOverride && sectionOverride.length > 0) {
            const orderMap = new Map(
              sectionOverride.map((id, idx) => [id, idx]),
            );
            customFields.sort((a, b) => {
              const ai = orderMap.get(a.field_id);
              const bi = orderMap.get(b.field_id);
              if (ai !== undefined && bi !== undefined) return ai - bi;
              if (ai !== undefined) return -1;
              if (bi !== undefined) return 1;
              return 0;
            });
          }
          const expanded = expandedSections[section.key] ?? false;
          return (
            <Accordion
              key={section.key}
              sx={ACCORDION_SX}
              expanded={expanded}
              onChange={(_, next) =>
                setExpandedSections((p) => ({ ...p, [section.key]: next }))
              }
            >
              <AccordionSummary expandIcon={null} sx={ACCORDION_SUMMARY_SX}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: expanded ? "#DBF8FF" : "transparent",
                      color: expanded ? "#0E9DBC" : "#3e3e3e",
                      transition: "all 0.2s",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "#DBF8FF",
                        "& svg": { color: "#0E9DBC" },
                      },
                    }}
                  >
                    <ChevronRightIcon
                      sx={{
                        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                        fontSize: 20,
                        color: "inherit",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0, gap: "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#3E3E3E" }}
                    >
                      {section.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.text.secondary,
                        backgroundColor: "#F5F5F5",
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      {customFields.length}件
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #E0E0E0", p: 2 }}>
                {section.fixedFields.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Box
                      onClick={() => toggleFixed(section.key)}
                      sx={[
                        {
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          width: "fit-content",
                          maxWidth: "100%",
                          borderRadius: "8px",
                          px: 0.5,
                          py: 0.25,
                          transition: "color 0.2s",
                        },
                        expandedFixed[section.key]
                          ? {
                              color: "#0E9DBC",
                              "& svg": { color: "#0E9DBC" },
                            }
                          : {
                              color: "#3E3E3E",
                              "&:hover": {
                                color: "#0E9DBC",
                                "& svg": { color: "#0E9DBC" },
                              },
                            },
                      ]}
                    >
                      {expandedFixed[section.key] ? (
                        <ExpandMoreIcon sx={{ fontSize: 14, color: "inherit" }} />
                      ) : (
                        <ChevronRightIcon
                          sx={{ fontSize: 14, color: "inherit" }}
                        />
                      )}
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 500, color: "inherit" }}
                      >
                        固定項目 ({section.fixedFields.length}件)
                      </Typography>
                    </Box>
                    <Collapse in={!!expandedFixed[section.key]}>
                      <Stack spacing={0.5}>
                        {section.fixedFields.map((f) => (
                          <Box
                            key={f.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.75,
                              fontSize: 12,
                              backgroundColor: "rgba(0,0,0,0.02)",
                              borderRadius: 1,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>{f.label}</Box>
                            {f.required && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#DA384C",
                                  fontWeight: 700,
                                }}
                              >
                                必須
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )}

                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#3E3E3E",
                      mb: 1,
                    }}
                  >
                    追加項目
                  </Typography>
                  {customFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event: DragEndEvent) =>
                        handleDragEndForSection(section.key, event)
                      }
                    >
                      <SortableContext
                        // BE 既存行 + ローカル新規 (isNew) も並び替え対象に含める。
                        items={customFields.map((cf) => cf.field_id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack spacing={1} useFlexGap sx={{ mb: 1 }}>
                          {customFields.map((f) => {
                            const draft = drafts[f.field_id];
                            if (!draft) return null;
                            return (
                              <SortableFieldRow
                                key={f.field_id}
                                fieldId={f.field_id}
                                labelRegister={editForm.register}
                                errors={editForm.formState.errors}
                                onDelete={() =>
                                  handleDeleteFieldClick(f.field_id)
                                }
                              />
                            );
                          })}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  )}
                  <Can action="config:edit">
                    <CustomButton
                      label="新しい項目を追加"
                      buttonCategory="clearPrimary"
                      onClick={() => handleAddCustomField(section.key)}
                      sx={{ width: "100%", borderStyle: "dashed" }}
                    />
                  </Can>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
      )}

      <CustomModal
        openModal={footerDeleteConfirmOpen}
        handleCloseModal={cancelFooterDeleteConfirm}
        title="削除の確認"
        modalType="deleteModal"
        bodyText=""
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: "#666", mb: 2 }}>
            {pendingDeleteFieldIds.length}
            件の項目を削除します。
            <br />
            この操作は取り消せません。削除してよろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={cancelFooterDeleteConfirm}
            buttonCategory="cancelModal"
          />
          <Can action="config:edit">
            <CustomButton
              label="削除する"
              onClick={() => void confirmFooterDeleteAndSave()}
              buttonCategory="deleteModal"
            />
          </Can>
        </Box>
      </CustomModal>

      <FixedActionBar
        submitLabel="更新"
        isLoading={footerSaving}
        disabled={!hasDirtyDrafts || footerSaving}
        onSubmit={() => void handleFooterUpdateClick()}
      />
    </Box>
  );
};

export default ManagementTaskCustomFormSection;
